import { Injectable } from '@angular/core';
import {
    NetworkUtilsService,
    URL_DICT,
} from 'projects/app-core/src/service/network-utils.service';
import { LyfisAnswerType } from '../pages/lyfis/models/lyfis-questions-grid.models';
import { Observable } from 'rxjs';
import {
    LyfisPartnerProfilesGridRequest,
    LyfisPartnerProfilesGridResponse,
} from '../pages/lyfis/models/lyfis-partner-profiles-grid.models';
import {
    LyfisQuestionsGridRequest,
    LyfisQuestionsGridResponse,
} from 'projects/admin/src/app/pages/lyfis/models/lyfis-questions-grid.models';
import { AnyoPacksGridRequest } from '../models/packs/AnyoPacksGridRequest';
import {
    LyfisPersistedUsersGridRequest,
    LyfisPersistedUsersGridResponse,
} from '../pages/lyfis/models/lyfis-persisted-users-grid.models';
import {
    LyfisPersistedUserScoresGridRequest,
    LyfisPersistedUserScoresGridResponse,
} from '../pages/lyfis/models/lyfis-persisted-user-scores-grid.models';
import {
    LyfisPersistedDiseaseScoresGridRequest,
    LyfisPersistedDiseaseScoresGridResponse,
} from '../pages/lyfis/models/lyfis-persisted-disease-scores-grid.models';

export interface QuestionBlockOption {
    id: string;
    name: string;
    categoryCode: string;
    category: string;
    questionCount: number;
}

export interface DiseaseOption {
    id: string;
    data: string;
    icdCode: string;
    active: boolean;
}

export interface RuleOption {
    id: string;
    name: string;
    type: string;
    description?: string;
}

export interface LyfisDropdownValuesResponse {
    questionBlocks: QuestionBlockOption[];
    enabledDiseases: DiseaseOption[];
    compiledArtifacts: CompiledArtifactOption[];
    pdhBandsRules: RuleOption[];
    ghsBandsRules: RuleOption[];
}

/**
 * Trace breakdown for a single questionnaire fact evaluation.
 */
export interface QuestionnaireTraceBreakdown {
    factKey: string;
    inputValue: unknown;
    evaluatedCondition: string;
    matched: boolean;
    pointsAwarded: number;
}

/**
 * Trace breakdown for a single lab marker evaluation.
 */
export interface LabTraceBreakdown {
    markerKey: string;
    inputValue: number | null;
    available: boolean;
    evaluatedCondition: string;
    matched: boolean;
    pointsAwarded: number;
}

/**
 * Trace for scenario evaluation and weight selection.
 */
export interface ScenarioTrace {
    evaluatedScenarios: Array<{
        scenarioName: string;
        condition: string;
        matched: boolean;
    }>;
    selectedScenario: string;
    weights: { wQ: number; wL: number };
}

/**
 * Complete trace of the scoring calculation for explainability.
 */
export interface ScoreTrace {
    questionnaireBreakdown: QuestionnaireTraceBreakdown[];
    labBreakdown: LabTraceBreakdown[];
    scenarioTrace: ScenarioTrace;
    totals: {
        rawQuestionnairePoints: number;
        maxQuestionnairePoints: number;
        rawLabPoints: number;
        maxLabPoints: number;
    };
}

export interface CompiledArtifactOption {
    id: string;
    diseaseId: string;
    rulesVersionId?: string;
    scorecardId?: string;
    updatedAt?: string;
    fileName?: string;
    compiledAt?: string;
}

export interface UpsertPartnerProfileRequest {
    id?: string;
    partnerId?: string;
    active?: boolean;
    enabledDiseases?: string[];
    diseaseArtifacts?: Array<{ diseaseId: string; compiledArtifactId: string }>;
    pdhBandsRules?: string[];
    ghsBandsRules?: string[];
}

export interface LyfisPartnerProfile {
    id: string;
    partnerId?: string | null;
    active: boolean;
    enabledDiseases: string[];
    diseaseArtifacts: Array<{ diseaseId: string; compiledArtifactId: string }>;
    pdhBandsRules: string[];
    ghsBandsRules: string[];
    createdAt?: string;
    updatedAt?: string;
}

export interface LyfisScorecardUploadRequest {
    diseaseId: string;
    fileName: string;
    fileSize: number;
    fileType: string;
}

export interface LyfisScorecardUploadResponse {
    uploadUrl: string;
    scorecardId: string;
}

export interface LyfisScorecard {
    scorecardId: string;
    diseaseId: string;
    fileName: string;
    uploadStatus: 'PENDING' | 'UPLOADING' | 'COMPLETED' | 'FAILED';
    compileStatus: 'ok' | 'rejected' | 'interpreting' | 'validating' | 'idle';
    compiledConfig?: Record<string, unknown>;
    compiledEngineCode?: string;
    rulesVersionId?: string;
    compiledArtifact?: {
        config: Record<string, unknown>;
        engineCode: string;
        rulesVersionId: string;
        scorecardId: string;
        diseaseId: string;
        updatedAt?: string;
    } | null;
}

export interface LyfisScorecardGridRow {
    scorecardId: string;
    diseaseId: string;
    diseaseName?: string;
    fileName: string;
    uploadStatus: 'PENDING' | 'UPLOADING' | 'COMPLETED' | 'FAILED';
    compileStatus: 'ok' | 'rejected' | 'interpreting' | 'validating' | 'idle';
    rulesVersionId?: string;
    compiledAt?: string;
    compiledArtifact?: LyfisScorecard['compiledArtifact'];
}

export interface LyfisScorecardsGridResponse {
    data: LyfisScorecardGridRow[];
    filterOptions?: Record<string, unknown>;
    pageNum: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
}

export interface ChatResponse {
    response: string;
    state: Record<string, unknown>;
}

export interface FactOption {
    id: string;
    label: string;
    factId?: string;
    diseaseIds?: string[];
}

export interface PartnerProfileOption {
    id: string;
    name: string;
    enabledDiseases?: string[];
}

export interface FactMetadata {
    factKey: string;
    answerType: LyfisAnswerType;
    description?: string;
    availableOptions?: Array<{ key: string; label: string }>;
    validation?: { min?: number; max?: number; regex?: string };
    placeholder?: string;
}

export interface LyfisDiseaseRequirementsResponse {
    compiledArtifactId: string;
    diseaseId: string;
    rulesVersionId: string;
    requiredQuestionnaireFacts: string[];
    requiredLabMarkers: string[];
    factMetadata?: FactMetadata[];
    labMarkerMetadata?: Array<{ markerKey: string; description?: string }>;
}

export interface LyfisPersistedTraceDetailResponse {
    snapshot: {
        id: string;
        userId: string;
        assessmentId: string;
        createdAt?: number;
        trigger?: 'lab' | 'questionnaire';
        ghs: number;
        ghsBand: string | null;
        globalInsightsGeneratingStatus?: string;
        globalInsightsError?: string;
        globalInsights?: unknown;
        partnerProfileId?: string;
        partnerId?: string;
        userTags?: string[];
        labReportIdsUsedCount: number;
    };
    disease: {
        id: string;
        diseaseId: string;
        diseaseName: string;
        drsScore: number | null;
        pdhScore: number | null;
        band: string | null;
        questionnaireScore: number | null;
        labMarkersScore: number | null;
        status?: string;
        rulesVersionId?: string;
        insightsGeneratingStatus?: string;
        insightsError?: string;
        insights?: unknown;
        createdAt?: number;
    };
    resolvedEngineInput: {
        questionnaireFacts: Array<{
            factKey: string;
            rawValue: unknown;
            displayValue: string;
        }>;
        labMarkersForDisease: Record<string, unknown>;
    };
    traceDisplay: {
        questionnaireBreakdown: Array<{
            factKey: string;
            displayInputValue: string;
            rawInputValue: unknown;
            evaluatedCondition: string;
            matched: boolean;
            pointsAwarded: number;
        }>;
        labBreakdown: Array<{
            markerKey: string;
            inputValue: number | null;
            available: boolean;
            evaluatedCondition: string;
            matched: boolean;
            pointsAwarded: number;
        }>;
        scenarioTrace?: ScoreTrace['scenarioTrace'];
        totals?: ScoreTrace['totals'];
    };
}

@Injectable({ providedIn: 'root' })
export class LyfisService {
    constructor(private api: NetworkUtilsService) { }

    public getDropdownValues() {
        return this.api.get<LyfisDropdownValuesResponse>(
            URL_DICT.adminLyfisDropdownValuesUrl,
        );
    }

    public upsertPartnerProfile(request: UpsertPartnerProfileRequest) {
        return this.api.post<LyfisPartnerProfile>(
            URL_DICT.adminLyfisPartnerProfilesUrl,
            request,
        );
    }

    public getPartnerProfile(id: string) {
        return this.api.get<UpsertPartnerProfileRequest>(
            `${URL_DICT.adminLyfisPartnerProfilesUrl}/${id}`,
        );
    }

    public getDiseaseRequirements(partnerProfileId: string, diseaseId: string) {
        return this.api.get<LyfisDiseaseRequirementsResponse>(
            `${URL_DICT.adminLyfisPartnerProfileDiseaseRequirementsUrl}/${partnerProfileId}/diseases/${diseaseId}/requirements`,
        );
    }

    public getPartnerProfilesGrid(
        req: LyfisPartnerProfilesGridRequest,
    ): Observable<LyfisPartnerProfilesGridResponse> {
        return this.api.post<LyfisPartnerProfilesGridResponse>(
            URL_DICT.adminLyfisPartnerProfilesGridUrl,
            req,
        );
    }

    public getQuestionsGrid(
        req: LyfisQuestionsGridRequest,
    ): Observable<LyfisQuestionsGridResponse> {
        return this.api.post<LyfisQuestionsGridResponse>(
            URL_DICT.adminLyfisQuestionsGridUrl,
            req,
        );
    }

    public upsertLyfisQuestion(request: {
        id?: string;
        internalLabel?: string;
        label?: string;
        order?: number;
        question?: string;
        categoryId?: string;
        diseaseIds?: string[];
        answerType?: LyfisAnswerType;
        factId?: string;
        options?: Array<{ key: string; label: string; order: number }>;
        validation?: { min: number; max: number; regex: string };
        active?: boolean;
        isStaticProfile?: boolean;
        cadence?: { minIntervalDays?: number };
    }) {
        return this.api.post(URL_DICT.adminLyfisQuestionsUrl, request);
    }

    public generateScorecardUploadUrl(req: LyfisScorecardUploadRequest) {
        return this.api.post<LyfisScorecardUploadResponse>(
            URL_DICT.lyfisScorecardUploadUrl,
            req,
        );
    }

    public updateScorecardUploadStatus(scorecardId: string, status: string) {
        return this.api.post<LyfisScorecard>(
            URL_DICT.lyfisScorecardUploadStatusUrl,
            { scorecardId, status },
        );
    }

    public chatWithCompiler(scorecardId: string, message: string) {
        return this.api.post<ChatResponse>(
            `${URL_DICT.lyfisScorecardCompileChatUrl}${scorecardId}/compile-chat`,
            { message },
        );
    }

    public getScorecard(scorecardId: string) {
        return this.api.get<LyfisScorecard>(
            URL_DICT.lyfisScorecardGetUrl + scorecardId,
        );
    }

    public listScorecards(req: AnyoPacksGridRequest) {
        return this.api.post<LyfisScorecardsGridResponse>(
            URL_DICT.lyfisScorecardGridUrl,
            req,
        );
    }

    // Convenience helpers for dropdown data
    public getPartnerProfilesForDropdown(pageSize = 100) {
        return this.getPartnerProfilesGrid({
            page: 1,
            pageSize,
            export: false,
        } as unknown as LyfisPartnerProfilesGridRequest);
    }

    public getFactOptions(pageSize = 500) {
        return this.getQuestionsGrid({
            page: 1,
            pageSize,
            export: false,
        } as unknown as LyfisQuestionsGridRequest);
    }

    public getScorecardCompilation(scorecardId: string) {
        return this.api.get<{
            scorecard: LyfisScorecard;
            compiledArtifact?: LyfisScorecard['compiledArtifact'];
            compileLogs: Array<Record<string, unknown>>;
            chatMessages: Array<{
                _id?: string;
                agentName: string;
                message: string;
                timestamp: string;
                role: string;
            }>;
        }>(`${URL_DICT.lyfisScorecardGetUrl}${scorecardId}/compilation`);
    }

    public getScorecardExcelDownloadUrl(scorecardId: string) {
        return this.api.get<{
            downloadUrl: string;
            fileName: string;
        }>(`${URL_DICT.lyfisScorecardGetUrl}${scorecardId}/download`);
    }

    public getScorecardChat(scorecardId: string) {
        return this.api.get<
            Array<{
                _id?: string;
                agentName: string;
                message: string;
                timestamp: string;
                role: string;
            }>
        >(`${URL_DICT.lyfisScorecardGetUrl}${scorecardId}/chat`);
    }

    public getLyfisPersistedUsersGrid(req: LyfisPersistedUsersGridRequest) {
        return this.api.post<LyfisPersistedUsersGridResponse>(
            URL_DICT.adminLyfisViewScoresUsersGridUrl,
            req,
        );
    }

    public getLyfisPersistedUserSnapshotsGrid(
        userId: string,
        req: LyfisPersistedUserScoresGridRequest,
    ) {
        return this.api.post<LyfisPersistedUserScoresGridResponse>(
            `${URL_DICT.adminLyfisViewScoresUserSnapshotsGridUrl}${userId}/snapshots/grid`,
            req,
        );
    }

    public getLyfisPersistedSnapshotDiseaseScoresGrid(
        snapshotId: string,
        req: LyfisPersistedDiseaseScoresGridRequest,
    ) {
        return this.api.post<LyfisPersistedDiseaseScoresGridResponse>(
            `${URL_DICT.adminLyfisViewScoresSnapshotDiseasesGridUrl}${snapshotId}/diseases/grid`,
            req,
        );
    }

    public getLyfisPersistedTraceDetail(snapshotId: string, diseaseScoreId: string) {
        return this.api.get<LyfisPersistedTraceDetailResponse>(
            `${URL_DICT.adminLyfisViewScoresSnapshotDiseasesGridUrl}${snapshotId}/diseases/${diseaseScoreId}/trace-detail`,
        );
    }

    public previewLyfisScores(payload: {
        partnerProfileId: string;
        questionnaireFacts: Record<string, unknown>;
        labMarkersByDisease: Record<
            string,
            Record<string, { value: number; reportDate?: string; daysSinceReport?: number }>
        >;
        diseases?: string[];
    }) {
        return this.api.post<{
            diseases: Array<{
                diseaseId: string;
                drsScore: number;
                pdhScore: number;
                questionnaireScore: number;
                labMarkersScore: number;
                band: string;
                wQ: number;
                wL: number;
                rulesVersionId: string;
                trace?: ScoreTrace | null;
            }>;
            ghs: number;
            ghsBand: string;
        }>(`${URL_DICT.lyfisScorecardGetUrl}preview`, payload);
    }

    public previewLyfisScoresBulk(payload: {
        partnerProfileId: string;
        diseases?: string[];
        scenarios: Array<{
            scenarioId?: string;
            questionnaireFacts: Record<string, unknown>;
            labMarkersByDisease: Record<
                string,
                Record<string, { value: number; reportDate?: string; daysSinceReport?: number }>
            >;
        }>;
    }) {
        return this.api.post<{
            results: Array<{
                scenarioId?: string;
                success: boolean;
                output?: {
                    diseases: Array<{
                        diseaseId: string;
                        drsScore: number;
                        pdhScore: number;
                        questionnaireScore: number;
                        labMarkersScore: number;
                        band: string;
                        wQ: number;
                        wL: number;
                        rulesVersionId: string;
                        trace?: ScoreTrace | null;
                    }>;
                };
                error?: string;
            }>;
        }>(`${URL_DICT.lyfisScorecardGetUrl}preview-bulk`, payload);
    }
}
